import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '__generated__/globalTypes';
import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { CloseButton } from 'atoms/buttons/CloseButton';
import { Horizontal } from 'atoms/layout/flex';
import { BodyS } from 'atoms/typography';
import { DiscountBannerWrapper } from 'components/referral/DiscountBannerWrapper';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useIntlContext } from 'contexts/intl';
import { useSportContext } from 'contexts/sport';
import { useWalletDrawerContext } from 'contexts/walletDrawer';
import { useIsTabletAndAbove } from 'hooks/device/useIsTabletAndAbove';
import { useAmountWithConversion } from 'hooks/useAmountWithConversion';
import { useConversionCredit } from 'hooks/useConversionCredit';
import { LIFECYCLE, useLifecycle } from 'hooks/useLifecycle';
import { glossary, marketCredits } from 'lib/glossary';
import { MonetaryAmount } from 'lib/monetaryAmount';

import { TermsDialog } from '../TermsDialog';

type Props = {
  sport?: Sport;
  rounded?: boolean;
};

const WalletLink = styled(ButtonBase)`
  text-decoration: underline;
`;
const TermsLink = styled(ButtonBase)`
  color: var(--c-white);
  text-decoration: underline;
`;

export const ConversionCreditBanner = ({ sport }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const { formatDistanceToNow } = useIntlContext();
  const sportContext = useSportContext();
  const { toggleDrawer } = useWalletDrawerContext();
  const { currentUser } = useCurrentUserContext();
  const conversionCredit = useConversionCredit(sport || sportContext.sport);
  const { main } = useAmountWithConversion({
    monetaryAmount: conversionCredit?.maxDiscount || MonetaryAmount.zero,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const { update, lifecycle } = useLifecycle();
  const hideConversionCreditBanner = lifecycle?.hideConversionCreditBanner;

  if (
    currentUser?.userSettings?.hideBalance ||
    hideConversionCreditBanner ||
    !conversionCredit
  )
    return null;

  const { endDate, percentageDiscount } = conversionCredit;

  const timeLeft = formatDistanceToNow(endDate, { addSuffix: false });

  return (
    <DiscountBannerWrapper
      rightNode={
        !!isTabletAndAbove && (
          <Horizontal>
            <CloseButton
              size="small"
              onClose={() => {
                update(LIFECYCLE.hideConversionCreditBanner, true);
              }}
            />
          </Horizontal>
        )
      }
    >
      <BodyS>
        <FormattedMessage
          {...marketCredits.haveDiscountCredits}
          values={{
            maxDiscount: main,
            percentageDiscount,
            timeLeft,
            walletLink: (...chunks) => (
              <WalletLink
                onClick={() => {
                  toggleDrawer();
                }}
              >
                {chunks}
              </WalletLink>
            ),
          }}
        />{' '}
        <TermsLink type="button" onClick={() => setOpenDialog(true)}>
          (
          <FormattedMessage {...glossary.termsApply} />)
        </TermsLink>
      </BodyS>
      {!!openDialog && <TermsDialog onClose={() => setOpenDialog(false)} />}
    </DiscountBannerWrapper>
  );
};
